import { TableRow, TableCell, Checkbox, Tooltip, Avatar } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { CellTable } from '../CellTable'
import { IHeader } from '../IHeader'
import { RowDefinition } from './RowDefinition'
import { Draggable } from 'react-beautiful-dnd'
import { ITableOrder } from '../CrudDataTable'
import { useAppSelector } from 'store/useStoreHook'
import tableRowStyle from 'styles/tableRow.style'
import { SxProps } from '@mui/system'
import { styled } from '@mui/material'
import { get } from 'lodash-es'
import useCollapse from '../useCollapse'
import TableCollapseButton from '../TableCollapseButton'
export interface IRowProps<T> {
    /**
     * header definition arrays
     */
    headers: IHeader[]
    /**
     * items to display in data table
     */
    item: T
    childrenItems?: T[]
    /**
     * property of item object that must be unique; e.g. ItemId, UserId
     */
    itemKey: string
    isSelect: boolean
    selectMode?: boolean
    editOnClick?: boolean
    onSelect?: (key: string) => void
    onToggleSwitch?: (key: string, toggleItem: T) => void
    onRowClick?: (item: T) => void
    onRowDblClick?: (item: T) => void
    rowOption?: RowDefinition<T>
    editPermission?: number[]
    hover?: boolean
    index: number
    enableDragAndDrop?: boolean
    sortColOrders?: ITableOrder[]
    hasCollapse?: boolean
}

const NoMaxWidthTooltip = styled(({ className, ...props }: any) => (
    <Tooltip {...props} classes={{ popper: className }} />
))({
    '& .MuiTooltip-tooltip': {
        maxWidth: 'none',
    },
})

const avatarStyle: SxProps = {
    backgroundColor: 'warning.main',
    color: 'topside.paper',
    height: 30,
    width: 30,
    ml: 1,
}

export interface IToggleableRowProps<T> extends IRowProps<T> {
    groupRowkey: string
    subRowLevel?: number
}

export function RowTableComponent<T>(props: IToggleableRowProps<T>) {
    const {
        headers,
        item,
        itemKey,
        isSelect = false,
        selectMode = true,
        editOnClick = true,
        onSelect,
        onToggleSwitch,
        onRowClick,
        onRowDblClick,
        rowOption,
        editPermission = [],
        hover,
        index,
        hasCollapse,
        childrenItems = [],
        enableDragAndDrop,
        groupRowkey,
    } = props
    const { openCollapse } = useCollapse(groupRowkey)

    const userData = useAppSelector((state) => state.auth.user)
    const [userAvatar, setUserAvatar] = useState<string>(
        userData?.firstName
            ? userData?.firstName?.slice(0, 1).toUpperCase() +
                  userData?.lastName?.slice(0, 1).toUpperCase()
            : '',
    )
    const rowOptionResolved = useMemo(() => rowOption || {}, [rowOption])
    const isDisabledRow = useMemo(
        () => rowOptionResolved.disabledRow?.(item) ?? false,
        [item, rowOptionResolved],
    )

    const handleOnEdit = useCallback(
        (e: any, item: T) => {
            if (e.detail === 1) {
                if (onRowClick) onRowClick(item)
            } else if (e.detail === 2) {
                if (onRowDblClick) onRowDblClick(item)
            }
        },
        [onRowClick, onRowDblClick],
    )

    const rowProps = useMemo(
        () => ({
            hover: hover,
            role: 'checkbox',
            classes: {
                hover: editOnClick ? 'pointer' : 'default',
            },
            selected: isSelect,
            onClick: (e: any) => {
                if (rowOption?.disabledClick?.(item) ?? false) return
                if (editOnClick) return handleOnEdit(e, item)
            },
        }),
        [editOnClick, handleOnEdit, hover, isSelect, item, rowOption],
    )

    const rowStyle = useMemo(() => {
        if (rowOptionResolved.conditionStyle)
            return rowOptionResolved.conditionStyle(item)
        return {}
    }, [item, rowOptionResolved])
    const DraggableComponent = (id: any, index: any, item: T) => (
        props: any,
    ) => {
        return (
            <Draggable draggableId={id} index={index}>
                {(provided, snapshot) => (
                    <Tooltip
                        title={rowOptionResolved.rowTooltip?.(item) ?? ''}
                        arrow
                    >
                        <TableRow
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            ref={provided.innerRef}
                            {...rowProps}
                            key={get(item, itemKey)}
                            sx={{
                                ...tableRowStyle.root,
                                display: snapshot.isDragging
                                    ? 'table'
                                    : 'table-row',
                                tableLayout: snapshot.isDragging
                                    ? 'fixed'
                                    : 'auto',
                            }}
                        >
                            {props.children}
                        </TableRow>
                    </Tooltip>
                )}
            </Draggable>
        )
    }

    const RowCheckBox = () => {
        // show tooltip if `disabledSelect` provided TRUE as result
        return (
            <NoMaxWidthTooltip
                title={
                    rowOptionResolved.disabledSelect &&
                    rowOptionResolved.disabledSelect(item)
                        ? rowOptionResolved.disabledHint
                        : ''
                }
                placement="bottom-start"
            >
                <span onClick={(e) => e.stopPropagation()}>
                    {rowOptionResolved.rowCheckbox?.(item) ?? (
                        <Checkbox
                            checked={isSelect}
                            onClick={(event) => {
                                event.stopPropagation()
                                onSelect?.(get(item, itemKey))
                            }}
                            color="primary"
                            disabled={
                                rowOptionResolved.disabledSelect &&
                                rowOptionResolved.disabledSelect(item)
                            }
                        />
                    )}
                </span>
            </NoMaxWidthTooltip>
        )
    }

    const clickAble =
        editOnClick && !(rowOption?.disabledClick?.(item) ?? false)

    let totalLeft = 0
    let isHiddenColumn = false

    useEffect(() => {
        setUserAvatar(
            userData?.firstName
                ? userData?.firstName?.slice(0, 1).toUpperCase() +
                      userData?.lastName?.slice(0, 1).toUpperCase()
                : '',
        )
    }, [userData])

    useEffect(() => {
        if (
            rowOptionResolved.disabledSelect &&
            rowOptionResolved.disabledSelect(item) &&
            isSelect
        )
            onSelect?.(get(item, itemKey))
    }, [rowOptionResolved, item, isSelect, onSelect, itemKey])

    return (
        <>
            {enableDragAndDrop ? (
                <TableRow
                    component={DraggableComponent(
                        'drag_id_' + get(item, itemKey),
                        index,
                        item,
                    )}
                    {...rowProps}
                    sx={tableRowStyle.root}
                >
                    {selectMode &&
                        (!rowOptionResolved.selectable ||
                        rowOptionResolved.selectable(item) ? (
                            <TableCell padding="checkbox">
                                <RowCheckBox />
                            </TableCell>
                        ) : (
                            <TableCell padding="none">
                                {userData?.userId === get(item, itemKey) && (
                                    <Avatar alt="User Name" sx={avatarStyle}>
                                        {userAvatar}
                                    </Avatar>
                                )}
                            </TableCell>
                        ))}
                    {headers.map((key: IHeader, indexKey: number) => {
                        totalLeft += headers[indexKey - 1]?.width ?? 0
                        isHiddenColumn = key.isDisabledRow ?? false

                        return (
                            !isHiddenColumn && (
                                <CellTable
                                    itemKey={key}
                                    item={item}
                                    rowOption={rowOption}
                                    onToggleSwitch={onToggleSwitch}
                                    width={key.width}
                                    key={indexKey}
                                    editPermission={editPermission}
                                    fixLeftNumber={totalLeft}
                                    disabledTooltip={key.disabledTooltip}
                                />
                            )
                        )
                    })}
                </TableRow>
            ) : (
                <>
                    <Tooltip
                        title={
                            (!(
                                rowOptionResolved.disabledSelect &&
                                rowOptionResolved.disabledSelect(item) &&
                                rowOptionResolved.disabledHint
                            ) &&
                                rowOptionResolved.rowTooltip?.(item)) ??
                            ''
                        }
                        arrow
                    >
                        <TableRow
                            {...rowProps}
                            hover={clickAble}
                            sx={
                                {
                                    ...tableRowStyle.root,
                                    ...rowStyle,
                                    ...(isDisabledRow &&
                                        tableRowStyle.disabled),
                                } as any
                            }
                        >
                            {hasCollapse && (
                                <TableCell padding="checkbox" width={48}>
                                    {(childrenItems?.length ?? 0) > 0 && (
                                        <TableCollapseButton
                                            groupRowkey={groupRowkey}
                                            openCollapse={openCollapse}
                                        />
                                    )}
                                </TableCell>
                            )}
                            {selectMode &&
                                (!rowOptionResolved.selectable ||
                                rowOptionResolved.selectable(item) ? (
                                    <TableCell padding="checkbox">
                                        <RowCheckBox />
                                    </TableCell>
                                ) : (
                                    <TableCell padding="none">
                                        {userData?.userId ===
                                            get(item, itemKey) && (
                                            <Avatar
                                                alt="User Name"
                                                sx={avatarStyle}
                                            >
                                                {userAvatar}
                                            </Avatar>
                                        )}
                                    </TableCell>
                                ))}
                            {headers.map((key: IHeader, indexKey: number) => {
                                totalLeft += headers[indexKey - 1]?.width ?? 0
                                isHiddenColumn = key.isDisabledRow ?? false

                                return (
                                    !isHiddenColumn && (
                                        <CellTable
                                            itemKey={key}
                                            item={item}
                                            rowOption={rowOption}
                                            onToggleSwitch={onToggleSwitch}
                                            width={key.width}
                                            key={indexKey}
                                            editPermission={editPermission}
                                            fixLeftNumber={totalLeft}
                                            disabledTooltip={
                                                key.disabledTooltip
                                            }
                                        />
                                    )
                                )
                            })}
                        </TableRow>
                    </Tooltip>
                    {openCollapse &&
                        childrenItems.map((row, i) => {
                            return (
                                <TableRow
                                    key={get(row, itemKey) ?? 'row-child-' + i}
                                >
                                    <TableCell width={48}></TableCell>

                                    {headers.map(
                                        (key: IHeader, indexKey: number) => {
                                            return (
                                                <CellTable
                                                    itemKey={key}
                                                    item={row}
                                                    width={key.width}
                                                    key={
                                                        indexKey +
                                                        (key.label?.toString() ||
                                                            '') +
                                                        '-row-child-cell'
                                                    }
                                                />
                                            )
                                        },
                                    )}
                                </TableRow>
                            )
                        })}
                </>
            )}
        </>
    )
}
